import {
  Box,
  Chip,
  Stack,
  Typography,
  alpha,
  useTheme,
  type Theme,
} from "@mui/material";
import {
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { NoDataWarning } from "../common/NoDataWarning";
import { ScheduleSliderDotsBalloon } from "../schedule/ScheduleSliderDotsBalloon";
import { GenericTableProps, TableAction, TableColumn } from "./types";
import { splitLegacyRowActions } from "./unwrapTableCellContent";
import { TableActionButton } from "./TableActionButton";

function renderFieldValue(value: unknown) {
  if (value == null || value === "" || value === "-") {
    return (
      <Typography variant="body2" color="text.disabled" fontSize="0.8125rem">
        —
      </Typography>
    );
  }
  if (isValidElement(value)) return value;
  return (
    <Typography
      variant="body2"
      fontWeight={600}
      fontSize="0.8125rem"
      sx={{ wordBreak: "break-word", color: "text.primary" }}
    >
      {String(value)}
    </Typography>
  );
}

function isStatusColumn(column: TableColumn) {
  return column.rowName === "ends" || column.column === "Status";
}

function StatusChip({ label }: { label: string }) {
  const normalized = String(label).toLowerCase();
  const isActive = normalized === "ativo" || normalized === "active";
  return (
    <Chip
      size="small"
      label={label}
      color={isActive ? "success" : "default"}
      variant={isActive ? "filled" : "outlined"}
      sx={{
        fontWeight: 700,
        height: 26,
        fontSize: "0.6875rem",
        borderRadius: 99,
      }}
    />
  );
}

function buildDisplayColumns(columns: TableColumn[]) {
  const hasDate = columns.some((c) => c.rowName === "date");
  const hasHour = columns.some((c) => c.rowName === "hour");

  if (!hasDate || !hasHour) return columns;

  const merged: TableColumn[] = [];
  let dateHourAdded = false;

  for (const column of columns) {
    if (column.rowName === "hour") continue;
    if (column.rowName === "date") {
      merged.push({ column: "Data e hora", rowName: "__dateHour" });
      dateHourAdded = true;
      continue;
    }
    merged.push(column);
  }

  if (hasHour && !dateHourAdded) {
    merged.push({ column: "Hora", rowName: "hour" });
  }

  return merged;
}

function getDisplayValue(row: Record<string, unknown>, column: TableColumn) {
  if (column.rowName === "__dateHour") {
    const date = row.date;
    const hour = row.hour;
    if (date && hour) return `${date} · ${hour}`;
    return date ?? hour ?? null;
  }
  return row[column.rowName];
}

/** Carrossel contido no pai — evita corte por 100vw/overflow. */
const mobileCarouselRootSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box" as const,
  overflow: "hidden",
};

/** Barra de ações edge-to-edge dentro do card (100% do slide). */
const mobileCardActionsBarSx = {
  display: "flex",
  flexDirection: "row" as const,
  alignItems: "stretch" as const,
  width: "100%",
  minWidth: "100%",
  flexShrink: 0,
  bgcolor: (theme: Theme) => alpha(theme.palette.grey[500], 0.06),
  borderTop: "1px solid",
  borderColor: "divider",
  "& > *": {
    flex: "1 1 0",
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
  },
  "& .MuiButtonBase-root": {
    width: "100% !important",
    maxWidth: "100% !important",
    borderRadius: "0 !important",
    border: "none !important",
    boxShadow: "none !important",
    minHeight: 52,
  },
  "& > *:not(:last-child) .MuiButtonBase-root": {
    borderRight: "1px solid",
    borderColor: "divider",
  },
};

function MobileCardActionsBar({
  children,
  onClickCapture,
}: {
  children: React.ReactNode;
  onClickCapture?: (event: React.MouseEvent) => void;
}) {
  return (
    <Box sx={mobileCardActionsBarSx} onClick={onClickCapture}>
      {children}
    </Box>
  );
}

/**
 * Carrossel de cards mobile — sem paginação/tabela, só swipe + dots.
 */
export function GenericTableMobileCards<T extends Record<string, unknown>>({
  tableColumns,
  tableRows,
  actions,
  onRowClick,
  mobileLayout = "carousel",
}: GenericTableProps<T>) {
  const theme = useTheme();
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const isSliderScrolling = useRef(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const slideCount = tableRows?.length ?? 0;
  const hasData = slideCount > 0;

  const primaryColumn =
    tableColumns.find((column) => column.rowName === "name") ??
    tableColumns.find((column) => column.rowName === "docType") ??
    tableColumns[0];
  const statusColumn = tableColumns.find(isStatusColumn);
  const detailColumns = useMemo(() => {
    const base = tableColumns.filter((column) => {
      if (column.rowName === primaryColumn?.rowName) return false;
      if (statusColumn && column.rowName === statusColumn.rowName) return false;
      return true;
    });
    return buildDisplayColumns(base);
  }, [primaryColumn?.rowName, statusColumn, tableColumns]);

  const scrollSliderToIndex = useCallback(
    (index: number) => {
      const el = sliderRef.current;
      if (!el || slideCount <= 1) return;
      isSliderScrolling.current = true;
      el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
      window.setTimeout(() => {
        isSliderScrolling.current = false;
      }, 320);
    },
    [slideCount]
  );

  const handleSliderScroll = useCallback(() => {
    if (isSliderScrolling.current) return;
    const el = sliderRef.current;
    if (!el || el.clientWidth <= 0) return;
    const nextIndex = Math.round(el.scrollLeft / el.clientWidth);
    if (nextIndex !== activeSlide && nextIndex >= 0 && nextIndex < slideCount) {
      setActiveSlide(nextIndex);
    }
  }, [activeSlide, slideCount]);

  useEffect(() => {
    setActiveSlide(0);
    if (sliderRef.current) sliderRef.current.scrollLeft = 0;
  }, [slideCount]);

  const renderRowActions = (row: T) => {
    if (!actions) return null;

    if (typeof actions === "function") {
      const raw = actions(row);
      if (!raw) return null;
      const { actionBar, extras } = splitLegacyRowActions(raw);
      if (!actionBar) return extras ? <>{extras}</> : null;
      return (
        <>
          <MobileCardActionsBar onClickCapture={(e) => e.stopPropagation()}>
            {actionBar}
          </MobileCardActionsBar>
          {extras}
        </>
      );
    }

    const visibleActions = (actions as TableAction<T>[]).filter(
      (action) => !action.isVisible || action.isVisible(row)
    );
    if (!visibleActions.length) return null;

    return (
      <MobileCardActionsBar onClickCapture={(e) => e.stopPropagation()}>
        {visibleActions.map((action, index) => {
          const disabled = action.isDisabled?.(row);
          return (
            <TableActionButton
              key={index}
              compact={false}
              icon={action.icon as ReactElement}
              label={action.tooltip || "Ação"}
              tone="primary"
              disabled={disabled}
              onClick={() => action.onClick(row)}
            />
          );
        })}
      </MobileCardActionsBar>
    );
  };

  const renderMobileCard = (row: T, index: number) => {
    const primaryValue = primaryColumn ? row[primaryColumn.rowName] : null;
    const statusValue = statusColumn ? row[statusColumn.rowName] : null;
    const isActiveStatus =
      statusValue != null &&
      !isValidElement(statusValue) &&
      String(statusValue).toLowerCase() === "ativo";
    const accentColor = isActiveStatus
      ? theme.palette.success.main
      : theme.palette.grey[400];

    const rowKey =
      (row as { id?: string; identifier?: string; relatedId?: string }).id ??
      (row as { identifier?: string }).identifier ??
      (row as { relatedId?: string }).relatedId ??
      `card-${index}`;

    const rowActions = renderRowActions(row);
    const hasTextStatusChip =
      statusValue != null &&
      statusValue !== "" &&
      !isValidElement(statusValue);

    return (
      <Box
        key={rowKey}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "hidden",
          cursor: onRowClick ? "pointer" : "default",
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              bgcolor: accentColor,
            }}
          />

          <Box
            sx={{
              position: "relative",
              flex: 1,
              pl: 2,
              pr: 1.5,
              pt: 1.35,
              pb: 1.25,
              minWidth: 0,
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            {hasTextStatusChip ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 12,
                  zIndex: 1,
                }}
              >
                <StatusChip label={String(statusValue)} />
              </Box>
            ) : null}

            <Typography
              variant="subtitle2"
              fontWeight={800}
              lineHeight={1.3}
              sx={{
                wordBreak: "break-word",
                fontSize: "0.9375rem",
                letterSpacing: "-0.01em",
                pr: hasTextStatusChip ? 6.5 : 0,
              }}
            >
              {isValidElement(primaryValue)
                ? primaryValue
                : String(primaryValue ?? "—")}
            </Typography>

            {isValidElement(statusValue) ? (
              <Box sx={{ mt: 0.75 }}>{statusValue}</Box>
            ) : null}

            {detailColumns.length > 0 ? (
              <Box
                sx={{
                  mt: 1.25,
                  pt: 1.15,
                  borderTop: "1px dashed",
                  borderColor: alpha(theme.palette.divider, 0.9),
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 1.15,
                }}
              >
                {detailColumns.map((column) => (
                  <Box key={column.rowName} sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        fontSize: "0.6875rem",
                        fontWeight: 500,
                        mb: 0.2,
                      }}
                    >
                      {column.column}
                    </Typography>
                    {renderFieldValue(getDisplayValue(row, column))}
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        </Box>

        {rowActions}
      </Box>
    );
  };

  if (!hasData) {
    return <NoDataWarning />;
  }

  if (mobileLayout === "vertical") {
    return (
      <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0 }}>
        {tableRows.map((row, index) => (
          <Box
            key={`vertical-${index}`}
            sx={{
              width: "100%",
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              bgcolor: "background.paper",
              boxShadow: "0 2px 12px rgba(15, 23, 42, 0.04)",
            }}
          >
            {renderMobileCard(row, index)}
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Box sx={mobileCarouselRootSx}>
      <Box
        ref={sliderRef}
        onScroll={handleSliderScroll}
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: slideCount > 1 ? "auto" : "hidden",
          overflowY: "hidden",
          scrollSnapType: slideCount > 1 ? "x mandatory" : "none",
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {tableRows.map((row, index) => (
          <Box
            key={`slide-${index}`}
            sx={{
              flex: "0 0 100%",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              scrollSnapAlign: "start",
              scrollSnapStop: "always",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            {renderMobileCard(row, index)}
          </Box>
        ))}
      </Box>

      {slideCount > 1 ? (
        <Box sx={{ py: 1, bgcolor: "background.default" }}>
          <ScheduleSliderDotsBalloon
            total={slideCount}
            activeIndex={activeSlide}
            onDotClick={(index) => {
              setActiveSlide(index);
              scrollSliderToIndex(index);
            }}
            compact
          />
        </Box>
      ) : null}
    </Box>
  );
}
